import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl, AbstractControl } from '@angular/forms';
import { PaymentoInfoService } from './paymento-info.service';
import { HttpParams } from '@angular/common/http';
import { NotificationsService } from 'angular2-notifications';
import { NgxSpinnerService } from 'ngx-spinner';
import { EncriptyUtilService } from '../../../services/encripty-util.service';
import { EImportacaoType } from '../table/EImportacaoType';
import { parseCurrency } from '../../utils/currency-utils';

interface ISaldoGlobal {
  cnpj: string;
  id: number;
  limite: number;
  limiteAtual: number;
  nomeFantasia: string;
  percMaximoReuso: number;
  percReuso30D: number;
  percReusoConta: number;
  percReusoParcelado: number;
  produtoDescricao: string;
  produtoId: number;
  razaoSocial: string;
  saldo: number;
  saldoAtual: number;
  saldoContaCorrente: number;
  saldoParcelado: number;
  saldoProximos30dias: number;
  status: string;
}

interface ISaldoExibido {
  saldoGlobal: number;
  saldoContaCorrente: number;
  saldoFuturo: number;
}

interface ISaldoRecebiveis {
  valorBrutoDisponivel: number;
  valorLiquidoDisponivel: number;
  valorTotalTaxas: number;
  saldo30dias: number;
  saldoMaior30dias: number;
}

@Component({
  selector: "ngx-payment-info-importacao",
  templateUrl: "./payment-info.component.html",
  styleUrls: ["./payment-info.component.scss"],
})
export class PaymentInfoComponent implements OnInit {
  @Input() paymentForm!: FormGroup;
  @Input() importacaoType: EImportacaoType = EImportacaoType.CARGA;

  public saldosExibidos: ISaldoExibido = {
    saldoContaCorrente: 0,
    saldoFuturo: 0,
    saldoGlobal: 0,
  };
  public customLogin = null;
  public idProdutoSelected: number;
  public exibeSaldos: boolean;
  public saldoGlobal: ISaldoGlobal = {} as ISaldoGlobal;
  public EImportacaoType = EImportacaoType;

  constructor(
    private paymentInfoService: PaymentoInfoService,
    private encryptService: EncriptyUtilService,
    private notifications: NotificationsService,
    private spinnerService: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.spinnerService.show();
    const resCustom = localStorage.getItem("bway-customLogin");
    this.customLogin = JSON.parse(resCustom);
    document.title = this.customLogin.title;
    const cnpj = this.encryptService.decriptyBySecretKey(
      localStorage.getItem("bway-enterprise-federal-id")
    );
    this.idProdutoSelected = this.encryptService.decriptyBySecretKey(
      localStorage.getItem("bway-product-id")
    );
    let params = new HttpParams();

    if (cnpj != null) {
      params = params.append("cnpj", cnpj);
    }

    this.buscarSaldos(params);
    this.initializeForm();
    this.calculateTotal();
  }

  private setControlState(control: AbstractControl, shouldDisable: boolean) {
    shouldDisable ? control.disable() : control.enable();
  }

  updateFormStatus(): void {
    const globalCtrl = this.paymentForm.get("global");
    const currentAccountCtrl = this.paymentForm.get("currentAccount");
    const futureCtrl = this.paymentForm.get("future");

    this.setControlState(globalCtrl, this.saldosExibidos.saldoGlobal <= 0);
    this.setControlState(
      currentAccountCtrl,
      !this.exibeSaldos || this.saldosExibidos.saldoContaCorrente <= 0
    );
    this.setControlState(
      futureCtrl,
      !this.exibeSaldos || this.saldosExibidos.saldoFuturo <= 0
    );

    this.spinnerService.hide();
  }

  buscarSaldos(params: HttpParams) {
    this.spinnerService.show();
    this.paymentInfoService.taxasTermos(
      params,
      (response: boolean) => {
        this.exibeSaldos = response;

        let params2 = new HttpParams();
        if (this.exibeSaldos) {
          params2 = params2.append("isReusoCorporativo", "true");
        }

        let saldoFuturo = 0;
        this.paymentInfoService.buscaSaldoGlobal(
          params2,
          (responseBuscaSaldo: ISaldoGlobal) => {
            this.paymentInfoService.buscarSaldoRecebiveis(
              null,
              (res: ISaldoRecebiveis) => {
                const saldoProximo =
                  res && res.saldo30dias != null ? res.saldo30dias : 0;
                const saldoParcelado =
                  res && res.saldoMaior30dias != null ? res.saldoMaior30dias : 0;
                saldoFuturo = Number(saldoProximo) + Number(saldoParcelado);
                this.atualizarSaldos(null, saldoFuturo, null);
              },
              (_error) => {
                saldoFuturo = 0;
                this.atualizarSaldos(null, saldoFuturo, null);
              }
            );
            this.atualizarSaldos(responseBuscaSaldo.saldoContaCorrente, saldoFuturo, responseBuscaSaldo.saldoAtual);
          },
          () => {
            this.spinnerService.hide();
          }
        );
      },
      (error) => {
        this.spinnerService.hide();
        this.notifications.error(error.response.msgRetorno);
      }
    );
  }

  atualizarSaldos(saldoContaCorrente: number | null, saldoFuturo: number | null, saldoGlobal: number | null) {
    this.saldosExibidos = {
      saldoContaCorrente: saldoContaCorrente ? saldoContaCorrente : this.saldosExibidos.saldoContaCorrente,
      saldoFuturo: saldoFuturo ? saldoFuturo : this.saldosExibidos.saldoFuturo,
      saldoGlobal: saldoGlobal ? saldoGlobal : this.saldosExibidos.saldoGlobal,
    };
    this.updateFormStatus();
  }

  initializeForm(): void {
    this.paymentForm.addControl("global", new FormControl(0));
    this.paymentForm.addControl("currentAccount", new FormControl(0));
    this.paymentForm.addControl("future", new FormControl(0));
    this.paymentForm.addControl("total", new FormControl(0));
  }

  private parseAndSetDefault(controlName: string): number {
    const control = this.paymentForm.get(controlName);
    const parsedValue = parseCurrency(control.value);

    if (isNaN(parsedValue)) {
      control.setValue("0", { emitEvent: false });
      return 0;
    }

    return parsedValue;
  }

  calculateTotal(): void {
    const global = this.parseAndSetDefault("global");
    const currentAccount = this.parseAndSetDefault("currentAccount");
    const future = this.parseAndSetDefault("future");

    const total = global + currentAccount + future;
    this.paymentForm
      .get("total")
      .setValue(total.toFixed(2), { emitEvent: true });
  }
}
